import { State } from "country-state-city";

export const WALLET_TYPES = [
  { value: "paypal", label: "PayPal", description: "PayPal account for payments", icon: "💙" },
  { value: "stripe", label: "Stripe Connect", description: "Bank account via Stripe Connect", icon: "🟣" },
  { value: "bank_ach", label: "Bank/ACH", description: "Direct bank transfer (domestic & international)", icon: "🏦" },
  { value: "crypto", label: "Cryptocurrency", description: "Digital currency wallet", icon: "₿" },
];
// Note: "wise" is no longer a standalone type. International bank accounts are added
// via bank_ach with a non-Stripe country, which routes through Wise automatically.

// Only US uses Stripe Financial Connections (bank login modal + US routing/account fields).
// All other countries route through Wise which fetches country-specific bank fields dynamically.
export const STRIPE_SUPPORTED_COUNTRIES = ['US'];

export const CRYPTO_CURRENCIES = [
  { value: "BTC", label: "Bitcoin (BTC)" },
  { value: "ETH", label: "Ethereum (ETH)" },
  { value: "USDC", label: "USD Coin (USDC)" },
  { value: "USDT", label: "Tether (USDT)" },
];

// STATE/PROVINCE RESOLUTION
// Curated state lists take priority over the country-state-city package
// to preserve existing ISO codes (AB, ON, NSW, etc.) users may already have
// saved in the database.
const HARDCODED_STATES = {
  US: [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
    { value: "DC", label: "Washington D.C" },
  ],
  CA: [
    { value: "AB", label: "Alberta" },
    { value: "BC", label: "British Columbia" },
    { value: "MB", label: "Manitoba" },
    { value: "NB", label: "New Brunswick" },
    { value: "NL", label: "Newfoundland and Labrador" },
    { value: "NS", label: "Nova Scotia" },
    { value: "ON", label: "Ontario" },
    { value: "PE", label: "Prince Edward Island" },
    { value: "QC", label: "Quebec" },
    { value: "SK", label: "Saskatchewan" },
    { value: "NT", label: "Northwest Territories" },
    { value: "NU", label: "Nunavut" },
    { value: "YT", label: "Yukon" },
  ],
  AU: [
    { value: "NSW", label: "New South Wales" },
    { value: "VIC", label: "Victoria" },
    { value: "QLD", label: "Queensland" },
    { value: "WA", label: "Western Australia" },
    { value: "SA", label: "South Australia" },
    { value: "TAS", label: "Tasmania" },
    { value: "ACT", label: "Australian Capital Territory" },
    { value: "NT", label: "Northern Territory" },
  ],
};

// Resolves state list for a country. Falls back to country-state-city package
// so new tier-2 countries (IN, BR, MX, etc.) get states automatically.
export const getStatesForCountry = (countryCode) => {
  if (!countryCode) return [];
  if (HARDCODED_STATES[countryCode]) return HARDCODED_STATES[countryCode];

  try {
    const states = State.getStatesOfCountry(countryCode);
    if (!states?.length) return [];
    return states
      .map((s) => ({ value: s.isoCode, label: s.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  } catch {
    return [];
  }
};
// TAX ID VALIDATION
// All validation regexes operate on a normalized value (uppercase,
// alphanumeric-only). Use `normalizeTaxIdForValidation` before testing.
export const normalizeTaxIdForValidation = (value) =>
  (value || "").toString().toUpperCase().replace(/[^A-Z0-9]/g, "");

// Loose fallback accepted for any unknown country or unknown tax ID type.
// Deliberately permissive so we never block a legitimate submission.
const GENERIC_TAX_ID_REGEX = /^[A-Z0-9]{4,30}$/;

// TIER-1 COUNTRY CONFIGURATIONS
// Full configuration with curated tax ID types, format regexes, and
// compliance messaging. Prioritized by expected creator volume.
const TIER1_CONFIGS = {
  US: {
    label: "United States",
    currency: "USD",
    requiresState: true,
    stateLabel: "State",
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "ssn", label: "Social Security Number (SSN)" },
      { value: "ein", label: "Employer Identification Number (EIN)" },
      { value: "itin", label: "Individual Taxpayer Identification Number (ITIN)" },
    ],
    taxIdValidation: /^\d{9}$/,
    taxIdPlaceholder: "XXX-XX-XXXX",
    taxForms: ["W9", "W8"],
    requirementMessage:
      "Tax information is required for all non-PayPal payment methods in the United States for IRS compliance.",
  },
  CA: {
    label: "Canada",
    currency: "CAD",
    requiresState: true,
    stateLabel: "Province",
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "sin", label: "Social Insurance Number (SIN)" },
      { value: "bn", label: "Business Number (BN)" },
    ],
    taxIdValidation: /^\d{9}$|^\d{9}[A-Z]{2}\d{4}$/,
    taxIdPlaceholder: "XXX-XXX-XXX",
    taxForms: ["T4A"],
    requirementMessage:
      "Tax information is required for non-PayPal payments for CRA compliance.",
  },
  GB: {
    label: "United Kingdom",
    currency: "GBP",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax Reference",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "nino", label: "National Insurance Number (NINO)" },
      { value: "utr", label: "Unique Taxpayer Reference (UTR)" },
      { value: "crn", label: "Company Registration Number" },
    ],
    taxIdValidation: /^[A-Z]{2}\d{6}[A-Z]$|^\d{10}$|^(\d{8}|[A-Z]{2}\d{6})$/,
    taxIdPlaceholder: "AB123456C or 1234567890",
    taxForms: [],
    requirementMessage: "Tax information is required for HMRC compliance.",
  },
  AU: {
    label: "Australia",
    currency: "AUD",
    requiresState: true,
    stateLabel: "State/Territory",
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax File Number",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "tfn", label: "Tax File Number (TFN)" },
      { value: "abn", label: "Australian Business Number (ABN)" },
    ],
    taxIdValidation: /^\d{8,9}$|^\d{11}$/,
    taxIdPlaceholder: "123456789 or 12345678901",
    taxForms: [],
    requirementMessage: "Tax information is required for ATO compliance.",
  },
  DE: {
    label: "Germany",
    currency: "EUR",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "steuer_id", label: "Steuer-ID (Personal Tax ID)" },
      { value: "steuernummer", label: "Steuernummer (Tax Number)" },
      { value: "ust_id", label: "USt-IdNr (VAT ID)" },
    ],
    taxIdValidation: /^\d{10,11}$|^DE\d{9}$/,
    taxIdPlaceholder: "12345678901 or DE123456789",
    taxForms: [],
    requirementMessage: "Tax information is required for German tax compliance.",
  },
  FR: {
    label: "France",
    currency: "EUR",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax Reference",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "nir", label: "Numéro de Sécurité Sociale" },
      { value: "siren", label: "SIREN Number" },
      { value: "siret", label: "SIRET Number" },
    ],
    taxIdValidation: /^\d{9}$|^\d{13,15}$|^FR[A-Z0-9]{2}\d{9}$/,
    taxIdPlaceholder: "1234567890123",
    taxForms: [],
    requirementMessage: "Tax information is required for French tax compliance.",
  },
  SG: {
    label: "Singapore",
    currency: "SGD",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "nric", label: "NRIC (National Registration Identity Card)" },
      { value: "fin", label: "FIN (Foreign Identification Number)" },
      { value: "uen", label: "UEN (Unique Entity Number)" },
      { value: "gst", label: "GST Registration Number" },
    ],
    taxIdValidation: /^[STFGM]\d{7}[A-Z]$|^\d{8,10}[A-Z]$|^[A-Z0-9]{9,10}$/,
    taxIdPlaceholder: "S1234567A or 201912345K",
    taxForms: [],
    requirementMessage: "Tax information is required for IRAS compliance.",
  },
  IN: {
    label: "India",
    currency: "INR",
    requiresState: true,
    stateLabel: "State",
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "pan", label: "PAN (Permanent Account Number)" },
      { value: "gstin", label: "GSTIN (Goods & Services Tax ID)" },
      { value: "aadhaar", label: "Aadhaar Number" },
    ],
    taxIdValidation: /^[A-Z]{5}\d{4}[A-Z]$|^\d{2}[A-Z]{5}\d{4}[A-Z]\d[A-Z\d]{2}$|^\d{12}$/,
    taxIdPlaceholder: "ABCDE1234F or 22AAAAA0000A1Z5",
    taxForms: [],
    requirementMessage: "Tax information is required for Income Tax Department compliance.",
  },
  JP: {
    label: "Japan",
    currency: "JPY",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "my_number", label: "My Number (Individual)" },
      { value: "corporate_number", label: "Corporate Number" },
    ],
    taxIdValidation: /^\d{12,13}$/,
    taxIdPlaceholder: "123456789012",
    taxForms: [],
    requirementMessage: "Tax information is required for NTA compliance.",
  },
  PH: {
    label: "Philippines",
    currency: "PHP",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "TIN",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [{ value: "tin", label: "Tax Identification Number (TIN)" }],
    taxIdValidation: /^\d{9,12}$/,
    taxIdPlaceholder: "123-456-789 or 123-456-789-000",
    taxForms: [],
    requirementMessage: "Tax information is required for BIR compliance.",
  },
  ID: {
    label: "Indonesia",
    currency: "IDR",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "NPWP",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [{ value: "npwp", label: "NPWP (Tax ID Number)" }],
    taxIdValidation: /^\d{15,16}$/,
    taxIdPlaceholder: "12.345.678.9-012.345",
    taxForms: [],
    requirementMessage: "Tax information is required for DJP compliance.",
  },
  BR: {
    label: "Brazil",
    currency: "BRL",
    requiresState: true,
    stateLabel: "State",
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "cpf", label: "CPF (Individual)" },
      { value: "cnpj", label: "CNPJ (Business)" },
    ],
    taxIdValidation: /^\d{11}$|^\d{14}$/,
    taxIdPlaceholder: "123.456.789-09 or 12.345.678/0001-90",
    taxForms: [],
    requirementMessage: "Tax information is required for Receita Federal compliance.",
  },
  AR: {
    label: "Argentina",
    currency: "ARS",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "CUIT / CUIL",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "cuit", label: "CUIT (Business)" },
      { value: "cuil", label: "CUIL (Individual)" },
    ],
    taxIdValidation: /^\d{11}$/,
    taxIdPlaceholder: "20-12345678-9",
    taxForms: [],
    requirementMessage: "Tax information is required for AFIP compliance.",
  },
  CL: {
    label: "Chile",
    currency: "CLP",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "RUT",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [{ value: "rut", label: "RUT (Rol Único Tributario)" }],
    taxIdValidation: /^\d{7,8}[0-9K]$/,
    taxIdPlaceholder: "12345678-9",
    taxForms: [],
    requirementMessage: "Tax information is required for SII compliance.",
  },
  CO: {
    label: "Colombia",
    currency: "COP",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "nit", label: "NIT (Business)" },
      { value: "cedula", label: "Cédula (Individual)" },
    ],
    taxIdValidation: /^\d{6,11}$/,
    taxIdPlaceholder: "123456789-0",
    taxForms: [],
    requirementMessage: "Tax information is required for DIAN compliance.",
  },
  PL: {
    label: "Poland",
    currency: "PLN",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "pesel", label: "PESEL (Individual)" },
      { value: "nip", label: "NIP (Business)" },
    ],
    taxIdValidation: /^\d{10,11}$|^PL\d{10}$/,
    taxIdPlaceholder: "12345678901 (PESEL) or 1234567890 (NIP)",
    taxForms: [],
    requirementMessage: "Tax information is required for KAS compliance.",
  },
  RO: {
    label: "Romania",
    currency: "RON",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "cnp", label: "CNP (Individual)" },
      { value: "cui", label: "CUI (Business)" },
    ],
    taxIdValidation: /^\d{13}$|^(RO)?\d{2,10}$/,
    taxIdPlaceholder: "1234567890123 or RO12345678",
    taxForms: [],
    requirementMessage: "Tax information is required for ANAF compliance.",
  },
  CZ: {
    label: "Czech Republic",
    currency: "CZK",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "rc", label: "Rodné číslo (Birth Number)" },
      { value: "dic", label: "DIČ (VAT Number)" },
    ],
    taxIdValidation: /^\d{9,10}$|^CZ\d{8,10}$/,
    taxIdPlaceholder: "1234567890 or CZ12345678",
    taxForms: [],
    requirementMessage: "Tax information is required for Czech tax compliance.",
  },
  UA: {
    label: "Ukraine",
    currency: "UAH",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "rnokpp", label: "RNOKPP (Individual)" },
      { value: "edrpou", label: "EDRPOU (Business)" },
    ],
    taxIdValidation: /^\d{8}$|^\d{10}$/,
    taxIdPlaceholder: "1234567890 or 12345678",
    taxForms: [],
    requirementMessage: "Tax information is required for Ukrainian State Tax Service compliance.",
  },
  HU: {
    label: "Hungary",
    currency: "HUF",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "adoazonosito", label: "Adóazonosító jel (Individual)" },
      { value: "adoszam", label: "Adószám (Business)" },
    ],
    taxIdValidation: /^\d{10,11}$|^HU\d{8}$/,
    taxIdPlaceholder: "8123456789 or 12345678-1-12",
    taxForms: [],
    requirementMessage: "Tax information is required for NAV compliance.",
  },
  AE: {
    label: "United Arab Emirates",
    currency: "AED",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "TRN",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [{ value: "trn", label: "TRN (Tax Registration Number)" }],
    taxIdValidation: /^\d{15}$/,
    taxIdPlaceholder: "100123456700003",
    taxForms: [],
    requirementMessage: "Tax information is required for FTA compliance.",
  },
  SA: {
    label: "Saudi Arabia",
    currency: "SAR",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "tin", label: "TIN (Tax Identification Number)" },
      { value: "vat", label: "VAT Number" },
    ],
    taxIdValidation: /^\d{10,15}$/,
    taxIdPlaceholder: "300123456700003",
    taxForms: [],
    requirementMessage: "Tax information is required for ZATCA compliance.",
  },
  TR: {
    label: "Turkey",
    currency: "TRY",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "tckn", label: "TCKN (Individual)" },
      { value: "vkn", label: "VKN (Business)" },
    ],
    taxIdValidation: /^\d{10,11}$/,
    taxIdPlaceholder: "12345678901 or 1234567890",
    taxForms: [],
    requirementMessage: "Tax information is required for GİB compliance.",
  },
  IL: {
    label: "Israel",
    currency: "ILS",
    requiresState: false,
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "Tax ID",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "teudat_zehut", label: "Teudat Zehut (Individual)" },
      { value: "tik_nikuyim", label: "Tik Nikuyim (Business)" },
    ],
    taxIdValidation: /^\d{9}$/,
    taxIdPlaceholder: "123456789",
    taxForms: [],
    requirementMessage: "Tax information is required for Israel Tax Authority compliance.",
  },
  MX: {
    label: "Mexico",
    currency: "MXN",
    requiresState: true,
    stateLabel: "State",
    requiresTaxId: true,
    requiresTaxIdType: true,
    taxIdLabel: "RFC",
    requiresTaxForNonPaypal: true,
    taxIdTypes: [
      { value: "rfc_fisica", label: "RFC (Individual)" },
      { value: "rfc_moral", label: "RFC (Business)" },
      { value: "curp", label: "CURP (Individual)" },
    ],
    taxIdValidation: /^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{2,3}$|^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/,
    taxIdPlaceholder: "ABCD123456XYZ",
    taxForms: [],
    requirementMessage: "Tax information is required for SAT compliance.",
  },
};

// TIER-2 COUNTRIES (GENERIC FALLBACK)

const TIER2_COUNTRIES = [
  // Asia
  { code: "PK", label: "Pakistan", currency: "PKR" },
  { code: "BD", label: "Bangladesh", currency: "BDT" },
  { code: "LK", label: "Sri Lanka", currency: "LKR" },
  { code: "NP", label: "Nepal", currency: "NPR" },
  { code: "CN", label: "China", currency: "CNY" },
  { code: "KR", label: "South Korea", currency: "KRW" },
  { code: "TW", label: "Taiwan", currency: "TWD" },
  { code: "HK", label: "Hong Kong", currency: "HKD" },
  { code: "MY", label: "Malaysia", currency: "MYR" },
  { code: "TH", label: "Thailand", currency: "THB" },
  { code: "VN", label: "Vietnam", currency: "VND" },
  { code: "KH", label: "Cambodia", currency: "KHR" },
  { code: "MM", label: "Myanmar", currency: "MMK" },
  { code: "LA", label: "Laos", currency: "LAK" },
  { code: "MN", label: "Mongolia", currency: "MNT" },
  // South America
  { code: "VE", label: "Venezuela", currency: "VES" },
  { code: "EC", label: "Ecuador", currency: "USD" },
  { code: "UY", label: "Uruguay", currency: "UYU" },
  { code: "PY", label: "Paraguay", currency: "PYG" },
  { code: "BO", label: "Bolivia", currency: "BOB" },
  { code: "GY", label: "Guyana", currency: "GYD" },
  { code: "SR", label: "Suriname", currency: "SRD" },
  // Eastern Europe
  { code: "SK", label: "Slovakia", currency: "EUR" },
  { code: "BG", label: "Bulgaria", currency: "BGN" },
  { code: "BY", label: "Belarus", currency: "BYN" },
  { code: "MD", label: "Moldova", currency: "MDL" },
  { code: "RU", label: "Russia", currency: "RUB" },
  { code: "LT", label: "Lithuania", currency: "EUR" },
  { code: "LV", label: "Latvia", currency: "EUR" },
  { code: "EE", label: "Estonia", currency: "EUR" },
  { code: "RS", label: "Serbia", currency: "RSD" },
  { code: "HR", label: "Croatia", currency: "EUR" },
  { code: "SI", label: "Slovenia", currency: "EUR" },
  { code: "BA", label: "Bosnia and Herzegovina", currency: "BAM" },
  { code: "ME", label: "Montenegro", currency: "EUR" },
  { code: "MK", label: "North Macedonia", currency: "MKD" },
  { code: "AL", label: "Albania", currency: "ALL" },
  // Middle East
  { code: "QA", label: "Qatar", currency: "QAR" },
  { code: "KW", label: "Kuwait", currency: "KWD" },
  { code: "BH", label: "Bahrain", currency: "BHD" },
  { code: "OM", label: "Oman", currency: "OMR" },
  { code: "JO", label: "Jordan", currency: "JOD" },
  { code: "LB", label: "Lebanon", currency: "LBP" },
];

// Generic config applied to every tier-2 country.
const GENERIC_TAX_CONFIG = {
  requiresState: false,
  requiresTaxId: true,
  requiresTaxIdType: false,
  taxIdLabel: "Tax ID / Registration Number",
  requiresTaxForNonPaypal: true,
  taxIdTypes: [{ value: "local_tax_id", label: "Local Tax ID" }],
  taxIdValidation: GENERIC_TAX_ID_REGEX,
  taxIdPlaceholder: "Enter your local tax identifier",
  taxForms: [],
  requirementMessage:
    "Tax information is required for compliance with your local tax authority.",
};

// ═══════════════════════════════════════════════════════════════════
// FINAL MERGED CONFIG
// ═══════════════════════════════════════════════════════════════════
const buildCountriesTaxConfig = () => {
  const result = {};

  // Tier 1: attach state list if requiresState but no explicit list
  for (const [code, config] of Object.entries(TIER1_CONFIGS)) {
    const finalConfig = { ...config };
    if (finalConfig.requiresState) {
      const states = getStatesForCountry(code);
      finalConfig.states = states;
      // Gracefully degrade if we somehow have no state data at all
      if (!states.length) {
        finalConfig.requiresState = false;
        delete finalConfig.states;
      }
    }
    result[code] = finalConfig;
  }

  // Tier 2: apply generic config + auto-detect states
  for (const { code, label, currency } of TIER2_COUNTRIES) {
    const states = getStatesForCountry(code);
    const hasStates = states.length > 0;
    result[code] = {
      ...GENERIC_TAX_CONFIG,
      label,
      currency,
      requiresState: false,
      ...(hasStates && {
        states,
        stateLabel: "State/Province",
      }),
    };
  }

  return result;
};

export const COUNTRIES_TAX_CONFIG = buildCountriesTaxConfig();

// Safe accessor that always returns a valid config (US fallback).
export const getCountryConfig = (countryCode) =>
  COUNTRIES_TAX_CONFIG[countryCode] || COUNTRIES_TAX_CONFIG.US;

export const SUPPORTED_COUNTRIES = Object.entries(COUNTRIES_TAX_CONFIG)
  .map(([code, cfg]) => ({
    value: code,
    label: cfg.label,
    currency: cfg.currency,
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Convert ISO-3166-1 alpha-2 code to Unicode flag emoji. 'US' → '🇺🇸'.
export const countryToFlag = (code) => {
  if (!code) return '';
  return code
    .toUpperCase()
    .replace(/./g, (ch) => String.fromCodePoint(0x1f1e6 - 65 + ch.charCodeAt(0)));
};

// Countries Wise supports for international payouts (excludes US — US uses Stripe).
// Source: https://wise.com/help/articles/2932150
export const WISE_SUPPORTED_COUNTRY_CODES = new Set([
  'AD','AE','AG','AI','AL','AM','AO','AR','AT','AU','AW','AZ',
  'BA','BB','BD','BE','BF','BG','BH','BI','BJ','BM','BN','BO',
  'BR','BS','BT','BW','BY','BZ','CA','CD','CF','CG','CH','CI',
  'CL','CM','CN','CO','CR','CV','CY','CZ','DE','DJ','DK','DM',
  'DO','DZ','EC','EE','EG','ER','ES','ET','FI','FJ','FK','FM',
  'FR','GA','GB','GD','GE','GH','GM','GN','GQ','GR','GT','GW',
  'GY','HK','HN','HR','HT','HU','ID','IE','IL','IN','IQ','IS',
  'IT','JM','JO','JP','KE','KG','KH','KI','KM','KN','KR','KW',
  'KY','KZ','LA','LB','LC','LI','LK','LR','LS','LT','LU','LV',
  'LY','MA','MC','MD','ME','MG','MK','ML','MM','MN','MO','MR',
  'MT','MU','MV','MW','MX','MY','MZ','NA','NE','NG','NI','NL',
  'NO','NP','NR','NZ','OM','PA','PE','PG','PH','PK','PL','PT',
  'PW','PY','QA','RO','RS','RW','SA','SB','SC','SD','SE','SG',
  'SI','SK','SL','SM','SN','SO','SR','ST','SV','SZ','TC','TD',
  'TG','TH','TJ','TL','TM','TN','TO','TR','TT','TV','TZ','UA',
  'UG','UY','UZ','VA','VC','VE','VN','VU','WS','YE','ZA','ZM','ZW',
]);

// Bank country selector list: US first (Stripe), then Wise-supported countries
// sorted alphabetically. Only countries present in COUNTRIES_TAX_CONFIG are included.
export const BANK_PAYOUT_COUNTRIES = [
  // US — handled by Stripe Financial Connections / manual ACH
  ...Object.entries(COUNTRIES_TAX_CONFIG)
    .filter(([code]) => code === 'US')
    .map(([code, cfg]) => ({
      value: code,
      label: cfg.label,
      currency: cfg.currency,
      flag: countryToFlag(code),
    })),
  // All other Wise-supported countries that exist in our tax config
  ...Object.entries(COUNTRIES_TAX_CONFIG)
    .filter(([code]) => code !== 'US' && WISE_SUPPORTED_COUNTRY_CODES.has(code))
    .map(([code, cfg]) => ({
      value: code,
      label: cfg.label,
      currency: cfg.currency,
      flag: countryToFlag(code),
    }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

// ═══════════════════════════════════════════════════════════════════
// COUNTRY-AWARE TAX ID VALIDATOR
// ═══════════════════════════════════════════════════════════════════
// Centralized validator that works for every country. Normalizes the
// input (strips punctuation, uppercases) and tests against:
//   1. A type-specific US rule (SSN/EIN/ITIN) if applicable
//   2. The country's regex
//   3. The generic fallback (alphanumeric, 4-30 chars) so we never
//      reject a legitimate submission due to a missing country rule.
export const validateTaxIdValue = (value, taxIdType, countryCode) => {
  if (!value) return false;

  const normalized = normalizeTaxIdForValidation(value);
  if (!normalized) return false;

  // US-specific rules stay strict (legacy behavior).
  if (taxIdType === "ssn" || taxIdType === "ein" || taxIdType === "itin") {
    return /^\d{9}$/.test(normalized);
  }

  const config = COUNTRIES_TAX_CONFIG[countryCode];
  if (config?.taxIdValidation) {
    if (config.taxIdValidation.test(normalized)) return true;
    // Fall through to generic check so users aren't hard-blocked by
    // over-strict country regex edge cases.
  }

  return GENERIC_TAX_ID_REGEX.test(normalized);
};

// ═══════════════════════════════════════════════════════════════════
// LEGACY EXPORTS (keep for backward compatibility)
// ═══════════════════════════════════════════════════════════════════
export const TAX_ID_TYPES = COUNTRIES_TAX_CONFIG.US.taxIdTypes;
export const US_STATES = HARDCODED_STATES.US;

export const PROMO_TYPES = [
  { key: "campaign_followup", label: "Campaign follow-ups" },
  { key: "reminder", label: "General reminders" },
  { key: "special_offer", label: "Special offers" },
  { key: "event_notification", label: "Events & webinars" },
];

export const NEWS_TYPES = [
  { key: "weekly_newsletter", label: "Weekly newsletter" },
  { key: "monthly_newsletter", label: "Monthly newsletter" },
];

export const emptyWalletForm = () => ({
  wallet_type: "",
  nickname: "",
  paypal_email: "",
  wise_email: "",
  wise_currency: "USD",
  wise_profile_id: "",
  wise_recipient_id: "",
  bank_account_type: "checking",
  bank_routing_number: "",
  bank_account_number: "",
  bank_account_holder_name: "",
  bank_name: "",
  bank_address: "",
  swift_code: "",
  iban: "",
  crypto_currency: "BTC",
  crypto_wallet_address: "",
  crypto_network: "mainnet",
});

export const emptyTaxForm = () => ({
  tax_id_type: "",
  tax_id_number: "",
  legal_name: "",
  business_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "US",
  tax_country: "US",
  w9_submitted: false,
  w8_submitted: false,
  backup_withholding: false,
});
